/**
 * The parts list.
 *
 * Sixty-three ordinary attachments across eleven slots, plus twenty-three mastery
 * conversions in `mastery.js`. Every entry states a genuine pro and a genuine con,
 * and that is a hard rule rather than a stylistic one: a part with no cost is a part
 * every build takes, which removes a slot from the game rather than adding a choice
 * to it. `auditCatalog()` at the bottom of this file checks it mechanically — a part
 * whose modifiers are all improvements fails the audit at module load.
 *
 * Magnitudes are held to roughly 5-15% for an ordinary part and about 20% for the
 * strong compound parts that cost two points. Anything larger than that is not a
 * percentage at all: fire-mode conversion, magazine capacity, suppression, thermal,
 * slug, launcher and overheat are *behaviour*, expressed as `set` modifiers and
 * resolved as separate logic.
 *
 * Names are ours. The functional archetypes are the common vocabulary of the genre;
 * nothing in here is copied from any particular game's parts list, and the numbers
 * are tuned against this game's own weapon table, which nothing else shares.
 *
 * ## Costs
 * `cost` is in build points, against a limit of 5 (8 when expanded). Ordinary parts
 * cost 1; a part that does two jobs at once, or one that changes behaviour outright,
 * costs 2. Mastery costs 2. The limit is what makes "which five" the interesting
 * question, and the two-point parts are what stop the answer being "the five best".
 */

import { add, mult, scale, set, validateMod, SLOT_ORDER } from './schema.js';

/* -------------------------------------------------------------------------- */
/* optics                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Optics are the one slot whose defining property is not a modifier.
 *
 * `zoom` is an absolute magnification and the resolver turns it into `ads.fovScale`
 * and `ads.sensScale` against the weapon's own iron-sight magnification, so a 3x on
 * a rifle and a 7x on a sniper both mean what they say without either weapon's
 * hand-tuned base FOV being overwritten. `adsTime` is a multiplier on the weapon's
 * shoulder time and is the *only* place an optic's ADS cost is written down — the
 * `ads.in` modifier is generated from it below, so the two cannot drift apart.
 *
 * The remaining fields are pure data for the HUD and the behaviour agent:
 * `scopeGlint`, `thermalMode`, `targetHighlight`, `rangefinder`, `dualZoom`,
 * `reticleId`.
 */
const OPTICS = [
  {
    id: 'op_micro', name: 'IRIS-1 MICRO DOT', unlockOrder: 2, cost: 1,
    blurb: 'A dot the size of a grain of rice on a tube that barely clears the rail.',
    optic: { zoom: 1.0, adsTime: 0.94, reticleId: 'dot_fine' },
    mods: [mult('spread.adsMoving', 0.12)],
    pro: 'Fastest sight on the rail.',
    con: 'Nothing to steady the picture with — the cone opens up on the move.',
  },
  {
    id: 'op_reflex', name: 'SLATE OPEN REFLEX', unlockOrder: 5, cost: 1,
    blurb: 'An open frame. No tube, no tunnel, both eyes stay in the fight.',
    optic: { zoom: 1.0, adsTime: 1.05, reticleId: 'dot_ring' },
    mods: [mult('spread.adsMoving', -0.12), add('flinch.resist', 0.05)],
    pro: 'Wide picture; steadier while moving and under fire.',
    con: 'Slower to the shoulder than a micro.',
  },
  {
    id: 'op_holo', name: 'HALLOW-3 HOLOSIGHT', unlockOrder: 9, cost: 1,
    blurb: 'A projected reticle floating a hundred metres downrange.',
    optic: { zoom: 1.25, adsTime: 1.09, reticleId: 'holo_circle' },
    mods: [mult('spread.adsMoving', -0.10), mult('recoil.random', -0.08)],
    pro: 'A touch of magnification without losing the room.',
    con: 'Heavier glass; the sight takes longer to arrive.',
  },
  {
    id: 'op_combat2', name: 'VECTOR 2X COMBAT SCOPE', unlockOrder: 13, cost: 1,
    blurb: 'Short, squat, and the last magnified sight you can still swing indoors.',
    optic: { zoom: 2.0, adsTime: 1.14, reticleId: 'chevron' },
    mods: [mult('spread.hip', 0.10)],
    pro: 'Real magnification at almost no handling cost.',
    con: 'Bulk on the rail spoils the hip pattern.',
  },
  {
    id: 'op_mid3', name: 'LONGVIEW 3X', unlockOrder: 18, cost: 1,
    fit: { classes: ['ar', 'smg', 'lmg', 'mr', 'sr'] },
    blurb: 'Three power. The distance where a rifle stops being a rifle.',
    optic: { zoom: 3.0, adsTime: 1.20, reticleId: 'mildot' },
    mods: [mult('ergonomics.adsMoveScale', -0.08), mult('spread.hip', 0.12)],
    pro: 'Holds a target at ranges iron sights guess at.',
    con: 'Slow up, and you walk slower behind it.',
  },
  {
    id: 'op_marksman4', name: 'PIKESTAFF 4X MARKSMAN', unlockOrder: 24, cost: 1,
    fit: { classes: ['ar', 'lmg', 'mr', 'sr'] },
    blurb: 'A long tube with a fine cross and no forgiveness in it.',
    optic: { zoom: 4.0, adsTime: 1.26, reticleId: 'cross_fine' },
    mods: [mult('recoil.random', -0.12), mult('ergonomics.adsMoveScale', -0.12), mult('spread.hip', 0.18)],
    pro: 'A clean picture makes the pattern easy to read and pull against.',
    con: 'Heavy, slow, and useless at conversational distance.',
  },
  {
    id: 'op_dual', name: 'BIFOCAL DZ-4 SWITCHSIGHT', unlockOrder: 29, cost: 2,
    fit: { classes: ['ar', 'lmg', 'mr', 'sr'] },
    blurb: 'Two sight pictures on one mount. A thumb switch chooses which.',
    optic: { zoom: 1.5, dualZoom: [1.5, 4.0], adsTime: 1.22, reticleId: 'dual_post' },
    mods: [mult('ergonomics.switchIn', 0.10), mult('spread.hip', 0.10)],
    pro: 'Close and far on the same weapon, no compromise magnification.',
    con: 'Two optics worth of weight, and the switch is not free.',
  },
  {
    id: 'op_thermal', name: 'EMBER 3.5X THERMAL', unlockOrder: 33, cost: 2,
    fit: { notClasses: ['sg'] },
    blurb: 'A cooled sensor. Everything warm is white and everything else is not there.',
    optic: { zoom: 3.5, adsTime: 1.24, reticleId: 'thermal_cross', thermalMode: true, targetHighlight: true },
    mods: [mult('ergonomics.adsMoveScale', -0.10), add('flinch.resist', -0.08)],
    pro: 'Targets separate from cover, smoke and shadow.',
    con: 'A washed screen and a bloom on every muzzle flash, including yours.',
  },
  {
    id: 'op_sniper7', name: 'OVERWATCH 7X', unlockOrder: 36, cost: 2,
    fit: { classes: ['mr', 'sr'] },
    blurb: 'Seven power and an objective lens that catches the sun.',
    optic: { zoom: 7.0, adsTime: 1.30, reticleId: 'sniper_grid', scopeGlint: true },
    mods: [mult('aimSway.holdTime', 0.25), mult('ergonomics.adsMoveScale', -0.15)],
    pro: 'The longest reach on offer, and a longer breath to use it.',
    con: 'A glint that tells the target exactly where you are.',
  },
  {
    id: 'op_range4', name: 'TALLYMAN 4X RANGEFINDER', unlockOrder: 38, cost: 2,
    fit: { classes: ['lmg', 'mr', 'sr'] },
    blurb: 'A laser rangefinder wired to the reticle. It reads out before you fire.',
    optic: { zoom: 4.0, adsTime: 1.28, reticleId: 'range_grid', rangefinder: true },
    mods: [mult('aimSway.amp', -0.18), mult('ergonomics.sprintOut', 0.12)],
    pro: 'Range on the glass and a steadier hold.',
    con: 'Electronics and batteries hang off the weapon; slower back into the fight.',
  },
];

/* -------------------------------------------------------------------------- */
/* muzzle                                                                      */
/* -------------------------------------------------------------------------- */

const MUZZLE = [
  {
    id: 'mz_comp', name: 'LATTICE COMPENSATOR', unlockOrder: 3, cost: 1,
    blurb: 'Ported top and sides. The gas goes up so the muzzle does not.',
    mods: [
      mult('recoil.climb30Deg', -0.11), mult('recoil.yawRatio', -0.08),
      mult('signature.radius', 0.12), mult('ads.in', 0.04),
    ],
    pro: 'Flattens the climb through the middle of a magazine.',
    con: 'Louder on the map, and a fraction slower to the shoulder.',
  },
  {
    id: 'mz_brake', name: 'ANVIL MUZZLE BRAKE', unlockOrder: 11, cost: 1,
    blurb: 'A slab of steel that throws the blast sideways at everyone beside you.',
    mods: [
      mult('recoil.climb30Deg', -0.16), mult('recoil.random', -0.10),
      mult('signature.radius', 0.25), mult('spread.hip', 0.14), mult('viewKick.up', 0.12),
    ],
    pro: 'The strongest recoil cut in the slot.',
    con: 'Enormous signature, a worse hip cone, and it slaps the view every shot.',
  },
  {
    id: 'mz_supp', name: 'NIGHTSHADE SUPPRESSOR', unlockOrder: 16, cost: 2,
    blurb: 'A baffle stack. Keeps you off the enemy minimap for as long as it lasts.',
    mods: [
      set('signature.minimap', false), set('signature.tracer', false),
      mult('signature.radius', -0.55),
      mult('muzzleVelocity', -0.08), mult('ads.in', 0.07), mult('damage.range', -0.06),
    ],
    pro: 'Firing no longer paints you.',
    con: 'Slower rounds, shorter damage range, slower up.',
  },
  {
    id: 'mz_supph', name: 'NIGHTSHADE-H MONOCORE', unlockOrder: 30, cost: 2,
    blurb: 'A monolithic core welded to a long tube. Silent and enormously nose-heavy.',
    mods: [
      set('signature.minimap', false), set('signature.tracer', false),
      mult('signature.radius', -0.65),
      mult('damage.range', 0.08), mult('recoil.climb30Deg', -0.08),
      mult('ads.in', 0.14), mult('ergonomics.sprintOut', 0.14), mult('ergonomics.adsMoveScale', -0.08),
    ],
    pro: 'Silent, and it lengthens the damage range rather than shortening it.',
    con: 'The heaviest muzzle in the game. Everything about handling gets worse.',
  },
  {
    id: 'mz_scatter', name: 'SCATTERGUARD PORT', unlockOrder: 21, cost: 1,
    blurb: 'A cone that bleeds gas forward and settles the weapon in one hand.',
    mods: [
      mult('spread.hip', -0.15), mult('spread.hipMoving', -0.15), mult('spread.hipSprint', -0.12),
      mult('recoil.climb30Deg', 0.12), mult('damage.range', -0.08),
    ],
    pro: 'The tightest hip cone available from the muzzle.',
    con: 'Climbs harder and reaches less far.',
  },
  {
    id: 'mz_choke', name: 'TIGHTLINE CHOKE', unlockOrder: 8, cost: 1,
    fit: { classes: ['sg'] },
    blurb: 'A constriction at the crown. The pattern closes when the stock is in the shoulder.',
    mods: [
      set('pelletSpreadAdsScale', 0.62), mult('pelletSpread', -0.08),
      mult('ads.in', 0.12), mult('spread.hip', 0.18),
    ],
    pro: 'A shouldered shell throws a much tighter pattern.',
    con: 'Slower to shoulder, and worse from the hip — which is where shotguns live.',
  },
];

/* -------------------------------------------------------------------------- */
/* barrel                                                                      */
/* -------------------------------------------------------------------------- */

const BARREL = [
  {
    id: 'br_velocity', name: 'HOTLINE VELOCITY BARREL', unlockOrder: 6, cost: 1,
    blurb: 'A faster twist and a tighter bore. The round leaves harder.',
    mods: [
      mult('muzzleVelocity', 0.22), mult('damage.range', 0.10),
      mult('ads.in', 0.08), mult('ergonomics.sprintOut', 0.10),
    ],
    pro: 'Flatter, faster rounds that hold their damage further out.',
    con: 'Weight forward: slower up and slower out of a sprint.',
  },
  {
    id: 'br_long', name: 'RECKONING LONG BARREL', unlockOrder: 20, cost: 2,
    blurb: 'Every centimetre of barrel the receiver will take.',
    mods: [
      mult('damage.range', 0.18), mult('penetration', 0.15),
      mult('ads.in', 0.14), mult('ergonomics.adsMoveScale', -0.10), mult('ergonomics.sprintOut', 0.14),
    ],
    pro: 'The largest range extension in the catalog, and it punches through more.',
    con: 'Handles like a fence post.',
  },
  {
    id: 'br_short', name: 'STUBNOSE SHORT BARREL', unlockOrder: 4, cost: 1,
    blurb: 'Cut back to the gas block. Loud, brief, and quick in the hands.',
    mods: [
      mult('ads.in', -0.12), mult('ergonomics.sprintOut', -0.15), mult('ergonomics.adsMoveScale', 0.08),
      mult('damage.range', -0.14), mult('muzzleVelocity', -0.12),
    ],
    pro: 'Everything about handling improves.',
    con: 'The damage range falls away and the rounds are slow.',
  },
  {
    id: 'br_hybrid', name: 'CROSSCUT HYBRID BARREL', unlockOrder: 15, cost: 1,
    blurb: 'A compromise profile — thin under the handguard, heavy at the crown.',
    mods: [
      mult('ads.in', -0.06), mult('damage.range', 0.06),
      mult('recoil.climb30Deg', 0.12), mult('spread.hip', 0.10),
    ],
    pro: 'A little of both ends without committing to either.',
    con: 'Nothing damps the climb, and the hip cone opens.',
  },
  {
    id: 'br_heavy', name: 'DEADWEIGHT HEAVY BARREL', unlockOrder: 26, cost: 2,
    blurb: 'A bull profile with no taper. It does not move when it goes off.',
    mods: [
      mult('recoil.climb30Deg', -0.14), mult('recoil.random', -0.15), mult('spread.adsMoving', -0.12),
      mult('ads.in', 0.12), mult('ergonomics.sprintOut', 0.18), mult('ergonomics.switchIn', 0.10),
    ],
    pro: 'The most predictable the weapon will ever be.',
    con: 'You feel every gram of it in every transition.',
  },
  {
    id: 'br_match', name: 'HARDPOINT MATCH BARREL', unlockOrder: 32, cost: 2,
    fit: { notClasses: ['sg'] },
    blurb: 'A hand-lapped match barrel throated for a heavier, slower load.',
    mods: [
      mult('damage.mult', 0.07), mult('penetration', 0.25),
      mult('rpm', -0.07), mult('ads.in', 0.10), mult('recoil.climb30Deg', 0.12),
    ],
    pro: 'More damage per round — enough to move a breakpoint on some weapons.',
    con: 'Slower rate of fire and a harder climb to hold.',
  },
];

/* -------------------------------------------------------------------------- */
/* underbarrel                                                                 */
/* -------------------------------------------------------------------------- */

const UNDERBARREL = [
  {
    id: 'ub_vert', name: 'PLUMBLINE VERTICAL GRIP', unlockOrder: 2, cost: 1,
    blurb: 'A straight grip under the handguard. The oldest answer to muzzle rise.',
    mods: [mult('recoil.climb30Deg', -0.12), mult('ads.in', 0.06), mult('spread.hip', 0.10)],
    pro: 'Direct, honest vertical recoil control.',
    con: 'Slower to the shoulder, wider at the hip.',
  },
  {
    id: 'ub_horiz', name: 'CROSSBAR STABILISER', unlockOrder: 12, cost: 1,
    blurb: 'A wide bar the support hand pulls back against.',
    mods: [
      mult('recoil.yawRatio', -0.22), mult('recoil.random', -0.12),
      mult('recoil.climb30Deg', 0.06), mult('ergonomics.adsMoveScale', -0.06),
    ],
    pro: 'Kills the horizontal wander that makes a pattern hard to learn.',
    con: 'Slightly more climb, and it slows you down while aimed.',
  },
  {
    id: 'ub_handstop', name: 'SKIRMISH HANDSTOP', unlockOrder: 7, cost: 1,
    blurb: 'A thumb rest and a stop. Nothing to grip, nothing to slow you.',
    mods: [
      mult('ergonomics.sprintOut', -0.16), mult('ergonomics.switchIn', -0.10),
      mult('ergonomics.adsMoveScale', 0.06), mult('recoil.climb30Deg', 0.14),
    ],
    pro: 'Into the fight faster from a sprint and from a swap.',
    con: 'Nothing is controlling the muzzle at all.',
  },
  {
    id: 'ub_deadcalm', name: 'DEADCALM HANDGUARD', unlockOrder: 23, cost: 1,
    blurb: 'A weighted, padded guard built for holding a position, not taking one.',
    mods: [
      mult('aimSway.amp', -0.25), mult('aimSway.holdTime', 0.30), mult('spread.adsMoving', -0.10),
      mult('ergonomics.sprintOut', 0.16), mult('ergonomics.adsMoveScale', -0.08),
    ],
    pro: 'Steadier glass and a longer breath.',
    con: 'You are slow to everything that is not already in front of you.',
  },
  {
    id: 'ub_grenade', name: 'HOWLER 40MM TUBE', unlockOrder: 34, cost: 2,
    fit: { classes: ['ar', 'lmg', 'mr'] },
    blurb: 'A single-shot 40 mm tube clamped under the handguard.',
    mods: [
      set('launcher', { kind: 'grenade', rounds: 1, velocity: 78, fuse: 0, radius: 4.2, damage: 130 }),
      mult('ergonomics.sprintOut', 0.22), mult('ads.in', 0.12), mult('ergonomics.adsMoveScale', -0.10),
    ],
    pro: 'An answer to cover that no bullet has.',
    con: 'Two kilos of dead weight hanging off the front.',
  },
  {
    id: 'ub_smoke', name: 'CINDERBOX SMOKE TUBE', unlockOrder: 27, cost: 1,
    fit: { classes: ['ar', 'lmg', 'mr'] },
    blurb: 'The same tube, loaded with screening smoke instead of high explosive.',
    mods: [
      set('launcher', { kind: 'smoke', rounds: 2, velocity: 62, fuse: 0.8, radius: 6.5, duration: 12 }),
      mult('ergonomics.sprintOut', 0.16), mult('ads.in', 0.08),
    ],
    pro: 'Breaks a sightline for twelve seconds without killing anyone.',
    con: 'Still a tube, still weight, and it does no damage at all.',
  },
];

/* -------------------------------------------------------------------------- */
/* stock, butt pad, comb                                                       */
/* -------------------------------------------------------------------------- */

const STOCK = [
  {
    id: 'st_skeleton', name: 'FLEETWOOD SKELETON STOCK', unlockOrder: 5, cost: 1,
    blurb: 'A milled frame with more hole than metal.',
    mods: [
      mult('ergonomics.sprintOut', -0.14), mult('ergonomics.switchIn', -0.08),
      mult('ergonomics.adsMoveScale', 0.10),
      mult('recoil.climb30Deg', 0.14), mult('spread.adsMoving', 0.12),
    ],
    pro: 'Light. You move and swap noticeably faster.',
    con: 'Nothing in the shoulder to brace against.',
  },
  {
    id: 'st_patrol', name: 'DRIFTER PATROL STOCK', unlockOrder: 14, cost: 1,
    blurb: 'An adjustable comb and length of pull set up for walking fire.',
    mods: [
      mult('ergonomics.adsMoveScale', 0.18), mult('spread.adsMoving', -0.10),
      mult('ads.in', 0.08), mult('recoil.climb30Deg', 0.06),
    ],
    pro: 'The best weapon in the game for shooting while walking.',
    con: 'Slower to the shoulder, and it does nothing for the climb.',
  },
  {
    id: 'st_fixed', name: 'IRONBACK FIXED STOCK', unlockOrder: 10, cost: 1,
    blurb: 'One solid piece from receiver to buttplate. It does not fold, adjust or give.',
    mods: [
      mult('recoil.climb30Deg', -0.15), mult('recoil.random', -0.12),
      mult('ergonomics.adsMoveScale', -0.12), mult('ergonomics.sprintOut', 0.12),
    ],
    pro: 'The most stable shoulder in the slot.',
    con: 'You are a gun emplacement that happens to have legs.',
  },
  {
    id: 'st_folding', name: 'HAIRTRIGGER FOLDING STOCK', unlockOrder: 19, cost: 1,
    blurb: 'Folds against the receiver. Deploys on the way up out of a sprint.',
    mods: [
      mult('ergonomics.sprintOut', -0.20), mult('ads.in', -0.06),
      mult('spread.hip', 0.16), mult('recoil.climb30Deg', 0.10),
    ],
    pro: 'The fastest transition from running to firing in the slot.',
    con: 'A folded stock is a worse hip cone and a worse climb.',
  },
  {
    id: 'st_none', name: 'STRIPPED RECEIVER', unlockOrder: 31, cost: 2,
    blurb: 'No stock at all. A buffer plate and a sling loop.',
    mods: [
      mult('ergonomics.sprintOut', -0.26), mult('ergonomics.switchIn', -0.16),
      mult('ergonomics.adsMoveScale', 0.16),
      mult('recoil.climb30Deg', 0.26), mult('spread.adsMoving', 0.25), mult('recoil.random', 0.20),
    ],
    pro: 'The most mobile the weapon can physically be.',
    con: 'Genuinely difficult to control past the third round.',
  },
];

const STOCK_PAD = [
  {
    id: 'sp_bulkhead', name: 'BULKHEAD FLINCH PAD', unlockOrder: 17, cost: 1,
    blurb: 'A thick sorbothane wedge that soaks up what comes back the other way.',
    mods: [
      add('flinch.resist', 0.28),
      mult('ergonomics.sprintOut', 0.10), mult('ergonomics.adsMoveScale', -0.06),
    ],
    pro: 'Being shot at moves your aim far less.',
    con: 'A brick on the back of the weapon.',
  },
  {
    id: 'sp_gravel', name: 'GRAVELBED RECOIL PAD', unlockOrder: 8, cost: 1,
    blurb: 'A shot-filled pad. Dead weight exactly where dead weight helps.',
    mods: [
      mult('recoil.climb30Deg', -0.10), mult('viewKick.up', -0.18),
      mult('ergonomics.switchIn', 0.08), mult('ads.in', 0.05),
    ],
    pro: 'Less climb and a much calmer view.',
    con: 'Slower to draw and slower to shoulder.',
  },
  {
    id: 'sp_slingshot', name: 'SLINGSHOT TACTICAL PAD', unlockOrder: 25, cost: 1,
    blurb: 'A hooked pad that locks under the armpit in a low, tucked stance.',
    mods: [
      mult('spread.adsMoving', -0.18), mult('spread.crouchScale', -0.08),
      mult('ergonomics.sprintOut', 0.12),
    ],
    pro: 'The tightest aimed cone on the move, and better still crouched.',
    con: 'Takes a beat to unhook when you need to run.',
  },
];

const COMB = [
  {
    id: 'cb_ridgeline', name: 'RIDGELINE CHEEK RISER', unlockOrder: 13, cost: 1,
    blurb: 'A raised comb that puts the eye behind the glass without craning.',
    mods: [
      add('flinch.resist', 0.18), mult('ads.in', -0.05),
      mult('aimSway.amp', 0.18), mult('spread.adsMoving', 0.10),
    ],
    pro: 'A firm weld: faster up and much harder to shake off a target.',
    con: 'A high cheek is a loose cheek — the sight wanders more.',
  },
  {
    id: 'cb_steady', name: 'STEADYHAND COMB', unlockOrder: 22, cost: 1,
    blurb: 'A wide, low comb with a chin shelf, cut for holding a lane.',
    mods: [
      mult('aimSway.amp', -0.22), mult('aimSway.holdScale', -0.25),
      mult('ads.in', 0.09), mult('ergonomics.adsMoveScale', -0.08),
    ],
    pro: 'The steadiest glass available, and a deeper held breath.',
    con: 'You are committed to the position you are already in.',
  },
  {
    id: 'cb_snap', name: 'SNAPCOMB LOW RISER', unlockOrder: 28, cost: 1,
    blurb: 'Cut away almost to the receiver. The head drops onto it rather than into it.',
    mods: [
      mult('ads.in', -0.11), mult('ergonomics.switchIn', -0.08),
      mult('recoil.random', 0.18), mult('spread.adsMoving', 0.14),
    ],
    pro: 'The fastest shoulder in the slot.',
    con: 'No weld worth the name. Everything is noisier.',
  },
];

/* -------------------------------------------------------------------------- */
/* magazine                                                                    */
/* -------------------------------------------------------------------------- */

const MAGAZINE = [
  {
    id: 'mg_flip', name: 'TWINSTACK FLIP CLAMP', unlockOrder: 6, cost: 1,
    blurb: 'Two magazines taped face to face. Flip, seat, keep shooting.',
    mods: [
      mult('reload.scale', -0.22),
      mult('ergonomics.sprintOut', 0.10), mult('ergonomics.adsMoveScale', -0.05),
    ],
    pro: 'The fastest reload in the slot, without adding a round.',
    con: 'A brick under the receiver that catches on everything.',
  },
  {
    id: 'mg_ext1', name: 'EXTENDED CELL I', unlockOrder: 9, cost: 1,
    blurb: 'A longer body and a stiffer spring. A third again in the weapon.',
    mods: [
      mult('magSize', 0.33),
      mult('reload.scale', 0.10), mult('ergonomics.sprintOut', 0.08),
    ],
    pro: 'A third more rounds between reloads.',
    con: 'Slower to reload and slower out of a sprint.',
  },
  {
    id: 'mg_ext2', name: 'EXTENDED CELL II', unlockOrder: 21, cost: 2,
    blurb: 'A drum, or as near a drum as the receiver will accept.',
    mods: [
      mult('magSize', 0.66),
      mult('reload.scale', 0.20), mult('ergonomics.sprintOut', 0.16), mult('ergonomics.adsMoveScale', -0.08),
    ],
    pro: 'Two thirds again in the weapon. Whole engagements without a reload.',
    con: 'Slow to reload, slow to move, and impossible to hide behind.',
  },
  {
    id: 'mg_fast', name: 'SPEEDLOAD CARRIER', unlockOrder: 15, cost: 1,
    blurb: 'A shortened, tapered body with a flared base. It falls out and drops in.',
    mods: [
      mult('reload.scale', -0.14), mult('ergonomics.switchIn', -0.08),
      mult('magSize', -0.15),
    ],
    pro: 'Faster reloads and a faster draw.',
    con: 'Fewer rounds to reload in the first place.',
  },
  {
    id: 'mg_belt', name: 'IRONFEED BELT BOX', unlockOrder: 35, cost: 2,
    fit: { classes: ['lmg'], weapons: ['carbon57'] },
    blurb: 'A hard box and a disintegrating belt. It will not stop until the barrel does.',
    mods: [
      mult('magSize', 0.80),
      set('overheat', { capacity: 1, perShot: 0.0125, coolRate: 0.34, coolDelay: 0.85, lockTime: 2.4 }),
      mult('reload.scale', 0.35), mult('ergonomics.sprintOut', 0.25), mult('ergonomics.adsMoveScale', -0.12),
    ],
    pro: 'Belt capacity. Suppression that does not pause.',
    con: 'The barrel overheats and locks you out, and reloading takes an age.',
  },
];

/* -------------------------------------------------------------------------- */
/* rear grip                                                                   */
/* -------------------------------------------------------------------------- */

const REAR_GRIP = [
  {
    id: 'rg_sprint', name: 'SPRINTGUARD REAR GRIP', unlockOrder: 3, cost: 1,
    blurb: 'A steep, high grip that keeps the muzzle up through a run.',
    mods: [mult('ergonomics.sprintOut', -0.20), mult('ads.in', 0.07)],
    pro: 'Firing sooner out of a sprint than anything else in the slot.',
    con: 'The grip angle fights the shoulder on the way up.',
  },
  {
    id: 'rg_quick', name: 'FASTHAND REAR GRIP', unlockOrder: 7, cost: 1,
    blurb: 'Stippled, undercut, with a shelf for the web of the hand.',
    mods: [
      mult('ergonomics.switchIn', -0.22), mult('ergonomics.switchOut', -0.15),
      mult('recoil.climb30Deg', 0.08),
    ],
    pro: 'The fastest weapon swap available.',
    con: 'A shallow grip gives the muzzle more room to walk.',
  },
  {
    id: 'rg_mobile', name: "RUNNER'S REAR GRIP", unlockOrder: 24, cost: 2,
    blurb: 'A slim, raked grip built around never standing still.',
    mods: [
      mult('ergonomics.sprintOut', -0.12), mult('ergonomics.switchIn', -0.12),
      mult('ergonomics.adsMoveScale', 0.08),
      mult('spread.hip', 0.14), mult('recoil.climb30Deg', 0.08),
    ],
    pro: 'Everything about getting into the fight improves at once.',
    con: 'Once you are in it, the weapon is worse.',
  },
  {
    id: 'rg_lock', name: 'LOCKGRIP STABILISER', unlockOrder: 16, cost: 1,
    blurb: 'A palm swell and a beavertail that lock the wrist straight.',
    mods: [
      mult('recoil.climb30Deg', -0.09), mult('recoil.yawRatio', -0.12),
      mult('ergonomics.switchIn', 0.12), mult('ergonomics.sprintOut', 0.10),
    ],
    pro: 'Less climb and much less horizontal drift.',
    con: 'Slow into the hand and slow out of a sprint.',
  },
  {
    id: 'rg_trueshot', name: 'TRUESHOT REAR GRIP', unlockOrder: 29, cost: 1,
    blurb: 'A rigid grip cut for a single, deliberate first round.',
    mods: [
      mult('spread.bloomGain', -0.35), mult('spread.maxBloom', -0.20),
      mult('ads.in', 0.08), mult('recoil.climb30Deg', 0.08),
    ],
    pro: 'The cone barely opens across a long string of fire.',
    con: 'Slower up, and it climbs harder while it stays accurate.',
  },
];

/* -------------------------------------------------------------------------- */
/* laser                                                                       */
/* -------------------------------------------------------------------------- */

const LASER = [
  {
    id: 'la_bright', name: 'BRIGHTLINE 1MW LASER', unlockOrder: 4, cost: 1,
    blurb: 'A visible beam. It shows you exactly where the muzzle is pointed.',
    mods: [
      mult('spread.hip', -0.18), mult('spread.hipMoving', -0.18),
      set('signature.tracer', true), mult('signature.radius', 0.15), mult('ads.in', 0.04),
    ],
    pro: 'The tightest hip cone in the slot.',
    con: 'A bright line pointing straight back at you.',
  },
  {
    // Was CANTED TACTICAL LASER, and it set a `stance.tactical` flag that nothing
    // read: there is no canted stance in this game, so the name, the blurb and the
    // pro line all promised a mechanic that did not exist. The three real mods are
    // unchanged. What they describe is the dark alternative to BRIGHTLINE — half
    // the cone benefit, none of the signature — so that is what it is now called.
    id: 'la_canted', name: 'DARKBEAM IR LASER', unlockOrder: 18, cost: 1,
    blurb: 'A near-infrared emitter. Invisible downrange, and awkward under the hand.',
    mods: [
      mult('spread.hip', -0.08),
      mult('ads.in', 0.06), mult('ergonomics.switchIn', 0.06),
    ],
    pro: 'Hip help that does not draw a line back to where you are standing.',
    con: 'Half of BRIGHTLINE’s cone, and slower both to the shoulder and into the hand.',
  },
  {
    id: 'la_snap', name: 'SNAPBEAM TRANSITION LASER', unlockOrder: 11, cost: 1,
    blurb: 'A pressure pad wired to come on the instant the weapon starts to rise.',
    mods: [
      mult('ads.in', -0.10),
      mult('spread.hip', 0.12), mult('signature.radius', 0.12),
    ],
    pro: 'A meaningfully faster shoulder for one point.',
    con: 'Worse at the hip, and it announces you.',
  },
  {
    id: 'la_long', name: 'LONGBEAM TARGET LASER', unlockOrder: 26, cost: 1,
    blurb: 'A collimated designator with a dot that stays a dot at a hundred metres.',
    mods: [
      mult('spread.hipMoving', -0.12), mult('spread.adsMoving', -0.14),
      mult('signature.radius', 0.20), mult('ergonomics.sprintOut', 0.08),
    ],
    pro: 'Steadier both aimed and moving.',
    con: 'The loudest laser on the map and slow out of a run.',
  },
  {
    id: 'la_ghost', name: 'GHOSTLIGHT IR LASER', unlockOrder: 20, cost: 1,
    // The one laser with no visible beam. `resolveInto` carries this through as
    // `def.laser.visible`, which is what the behaviour module draws off.
    laserVisible: false,
    blurb: 'Infrared. Invisible to the naked eye and light enough to forget.',
    mods: [
      mult('ergonomics.sprintOut', -0.12), mult('ergonomics.switchIn', -0.10),
      mult('ergonomics.adsMoveScale', 0.06),
      mult('spread.hip', 0.10),
    ],
    pro: 'Pure handling, and nothing to see.',
    con: 'No aiming help at all — the hip cone gets worse, not better.',
  },
];

/* -------------------------------------------------------------------------- */
/* fire mod                                                                    */
/* -------------------------------------------------------------------------- */

const FIRE_MOD = [
  {
    id: 'fm_fmj', name: 'HARDJACKET ROUNDS', unlockOrder: 10, cost: 1,
    blurb: 'A hardened steel core in a full jacket. Made for what is behind the target.',
    mods: [
      mult('penetration', 0.45), mult('damage.range', 0.06),
      mult('ads.in', 0.08), mult('reload.scale', 0.08),
    ],
    pro: 'Goes through cover that stops everything else.',
    con: 'Heavy rounds: slower magazines and a slower shoulder.',
  },
  {
    id: 'fm_hivel', name: 'OVERPRESSURE ROUNDS', unlockOrder: 14, cost: 1,
    blurb: 'Loaded hot. More pressure than the receiver was designed around.',
    mods: [
      mult('muzzleVelocity', 0.30), mult('damage.range', 0.08),
      mult('recoil.climb30Deg', 0.14), mult('spread.hip', 0.10),
    ],
    pro: 'Very fast rounds that hold their damage further out.',
    con: 'The weapon fights back harder for it.',
  },
  {
    id: 'fm_springs', name: 'DAMPENED SPRING KIT', unlockOrder: 12, cost: 1,
    blurb: 'A heavier buffer and a progressive recoil spring.',
    mods: [
      mult('recoil.climb30Deg', -0.13), mult('recoil.recovery', 0.18),
      mult('rpm', -0.06),
    ],
    pro: 'Less climb and a view that settles faster.',
    con: 'A heavier carrier cycles slower. You lose rate of fire.',
  },
  {
    id: 'fm_rapid', name: 'OPEN-BOLT TRIGGER KIT', unlockOrder: 27, cost: 2,
    fit: { notFireModes: ['pump', 'bolt'] },
    blurb: 'A lightened carrier and a cut sear. It cycles faster than it was meant to.',
    mods: [
      mult('rpm', 0.14),
      mult('recoil.climb30Deg', 0.18), mult('spread.bloomGain', 0.30), mult('magSize', -0.08),
    ],
    pro: 'The only rate-of-fire increase in the game.',
    con: 'Harder to hold, the cone opens faster, and the magazine is shorter.',
  },
  {
    id: 'fm_incend', name: 'EMBERTIP ROUNDS', unlockOrder: 31, cost: 2,
    fit: { notClasses: ['sr'] },
    blurb: 'An incendiary compound in the nose. It burns on the way in.',
    mods: [
      mult('damage.mult', 0.06),
      mult('muzzleVelocity', -0.14), mult('damage.range', -0.10), mult('penetration', -0.25),
    ],
    pro: 'More damage per round on flesh.',
    con: 'A soft, slow projectile that stops in cover and dies off at range.',
  },
  {
    id: 'fm_slug', name: 'MONOSLUG SHELLS', unlockOrder: 23, cost: 2,
    fit: { classes: ['sg'] },
    blurb: 'One rifled lead slug instead of a handful of shot.',
    mods: [
      set('pellets', 1), set('pelletSpread', 0),
      scale('damage.mult', 0.60), mult('damage.range', 1.2),
      mult('muzzleVelocity', 0.25), mult('penetration', 0.60),
      mult('recoil.climb30Deg', 0.25), mult('ads.in', 0.10),
    ],
    pro: 'A shotgun that hits a man-sized target at rifle distance.',
    con: 'One projectile, no forgiveness, and the one-shot kill is gone for good.',
  },
  {
    id: 'fm_auto', name: 'FULL-AUTO SEAR', unlockOrder: 33, cost: 2,
    fit: { classes: ['ar', 'smg', 'mr'], fireModes: ['semi', 'burst'] },
    blurb: 'A sear that never resets. The trigger stops being a decision.',
    mods: [
      set('fireMode', 'auto'), scale('rpm', 0.68),
      mult('recoil.climb30Deg', 0.22), mult('spread.bloomGain', 0.25), mult('ads.in', 0.06),
    ],
    pro: 'Sustained automatic fire from a weapon that had none.',
    con: 'Much slower per round than the burst it replaces, and far harder to hold.',
  },
  {
    id: 'fm_burst', name: 'BURST CAM', unlockOrder: 25, cost: 2,
    fit: { classes: ['ar', 'smg', 'lmg', 'mr'], fireModes: ['auto', 'semi'] },
    // The cost is the dead trigger between bursts, which is a `set` and therefore
    // invisible to the numeric audit. Declared rather than faked with a token
    // percentage penalty nobody would ever feel.
    structuralCon: true,
    blurb: 'A cam and a counter. Three rounds a pull, faster than the weapon runs open.',
    mods: [
      set('fireMode', 'burst'), set('burstCount', 3), set('burstDelay', 0.20),
      scale('rpm', 1.30), mult('recoil.climb30Deg', -0.12),
    ],
    pro: 'Three rounds downrange before the muzzle knows about it, and a flatter climb.',
    con: 'A fifth of a second of dead trigger after every burst — a slower kill on\n         anything that needs more rounds than one burst holds.',
  },
  {
    id: 'fm_semi', name: 'SINGLE-FIRE SEAR', unlockOrder: 19, cost: 1,
    fit: { fireModes: ['auto', 'burst'] },
    blurb: 'A disconnector that insists on one round per pull.',
    mods: [
      set('fireMode', 'semi'), scale('rpm', 1.15),
      mult('spread.bloomGain', -0.40), mult('recoil.climb30Deg', -0.18),
      mult('recoil.random', 0.10),
    ],
    pro: 'A tight, flat, deliberate weapon that barely blooms.',
    con: 'No automatic fire. Every round costs a trigger pull.',
  },
];

/* -------------------------------------------------------------------------- */
/* assembly                                                                    */
/* -------------------------------------------------------------------------- */

const BY_SLOT = {
  optic: OPTICS,
  muzzle: MUZZLE,
  barrel: BARREL,
  underbarrel: UNDERBARREL,
  stockPad: STOCK_PAD,
  magazine: MAGAZINE,
  comb: COMB,
  rearGrip: REAR_GRIP,
  stock: STOCK,
  laser: LASER,
  fireMod: FIRE_MOD,
};

/**
 * An optic's ADS cost is written once, as `optic.adsTime`, and the `ads.in` modifier
 * is generated from it here. Typing both would be two numbers that mean one thing,
 * and they would disagree the first time either was retuned.
 */
function finish(slot, entry) {
  const mods = entry.mods ? entry.mods.slice() : [];
  if (entry.optic && entry.optic.adsTime != null && entry.optic.adsTime !== 1) {
    mods.push(mult('ads.in', entry.optic.adsTime - 1));
  }
  for (const m of mods) validateMod(m, entry.id);
  return {
    ...entry,
    slot,
    mods,
    cost: entry.cost ?? 1,
    fit: entry.fit ?? null,
    mastery: false,
    optic: entry.optic ?? null,
  };
}

/** Every ordinary attachment, keyed by id. Mastery is merged in by `index.js`. */
export const CATALOG = {};
for (const slot of SLOT_ORDER) {
  for (const entry of BY_SLOT[slot]) {
    if (CATALOG[entry.id]) throw new Error(`[attachments] duplicate id "${entry.id}"`);
    CATALOG[entry.id] = finish(slot, entry);
  }
}

/** Ids per slot, in catalog order. */
export const SLOT_CONTENTS = {};
for (const slot of SLOT_ORDER) SLOT_CONTENTS[slot] = BY_SLOT[slot].map((e) => e.id);

/* -------------------------------------------------------------------------- */
/* the no-free-lunch audit                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Which direction is *better* for each path.
 *
 * Used by `auditCatalog` to check the one rule the whole catalog rests on: every
 * part must have at least one modifier that makes the weapon worse. Without a
 * mechanical check this decays — a part gets retuned, its downside gets softened to
 * fix a complaint, and three patches later it is a free upgrade that every build
 * takes. `set` modifiers are exempt: they are behaviour, and whether full-auto is
 * "better" than burst is exactly the question the player is being asked.
 */
const BETTER_WHEN_LOWER = new Set([
  'ads.in', 'ads.out', 'reload.scale',
  'spread.adsMoving', 'spread.hip', 'spread.hipMoving', 'spread.hipSprint',
  'spread.crouchScale', 'spread.airScale', 'spread.maxBloom', 'spread.bloomGain',
  'recoil.climb30Deg', 'recoil.yawRatio', 'recoil.random', 'recoil.kick',
  'recoil.adsScale', 'recoil.crouchScale',
  'ergonomics.sprintOut', 'ergonomics.swayScale', 'ergonomics.switchIn', 'ergonomics.switchOut',
  'viewKick.up', 'viewKick.back',
  'aimSway.amp', 'aimSway.holdScale',
  'signature.radius', 'pelletSpread', 'pelletSpreadAdsScale',
  'burstDelay', 'pumpTime', 'boltCycle',
]);

/** Numeric modifier -> -1 worse, +1 better, 0 neutral. */
export function modDirection(m) {
  if (m.kind === 'set') return 0;
  const delta = m.kind === 'scale' ? m.value - 1 : m.value;
  if (delta === 0) return 0;
  const lowerIsBetter = BETTER_WHEN_LOWER.has(m.path);
  const improves = lowerIsBetter ? delta < 0 : delta > 0;
  return improves ? 1 : -1;
}

/**
 * Check every part for a genuine pro and a genuine con. Returns the offenders.
 * A part whose only downside is behavioural must say so with `structuralCon: true`.
 */
export function auditCatalog(entries) {
  const bad = [];
  for (const a of Object.values(entries)) {
    let pros = 0;
    let cons = 0;
    // An optic's upside is its sight picture — magnification, a thermal channel, a
    // rangefinder — none of which is a number on the weapon. Counted as behaviour.
    let sets = a.optic ? 1 : 0;
    for (const m of a.mods) {
      const d = modDirection(m);
      if (d > 0) pros++;
      else if (d < 0) cons++;
      else sets++;
    }
    if (pros === 0 && sets === 0) bad.push({ id: a.id, why: 'no upside at all' });
    if (cons === 0 && !a.structuralCon) bad.push({ id: a.id, why: 'no numeric downside' });
  }
  return bad;
}
